// src/components/Admin/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Tab, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserAuth } from '../../context/UserAuthContext';
import {
  FaUsers, FaImage, FaComment, FaHeart,
  FaChartBar, FaCog, FaExclamationTriangle
} from 'react-icons/fa';
import api from '../../services/api';
import PhotoManagement from './PhotoManagement';
import WishManagement from './WishManagement';
import UserManagement from './UserManagement';
import Settings from './Settings';
import Statistics from './Statistics';
import './Admin.css';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('statistics');
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is admin
    const checkAdminStatus = async () => {
      try {
        if (!user) {
          navigate('/login');
          return;
        }

        try {
          // Try to access admin endpoint to verify admin status
          await api.get('/admin/settings');
          setLoading(false);
        } catch (err) {
          // If access denied, user is not admin
          console.error('Admin access check failed:', err);
          setError('You do not have admin permissions');
          navigate('/');
        }
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Authentication error');
      }
    };

    checkAdminStatus();
  }, [user, navigate]);

  if (loading) {
    return (
        <div className="admin-loading-screen">
          <div className="admin-loading-content">
            <Spinner animation="border" role="status" className="admin-spinner">
              <span className="visually-hidden">{t('loading')}</span>
            </Spinner>
            <p className="admin-loading-text">{t('adminDashboard.verifyingAccess')}</p>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="admin-error-container">
          <div className="admin-error-content">
            <div className="admin-error-icon">
              <FaExclamationTriangle />
            </div>
            <h2 className="admin-error-title">{t('adminDashboard.accessDenied')}</h2>
            <p className="admin-error-message">{error}</p>
            <p className="admin-error-detail">{t('adminDashboard.errorCode')}: 403</p>
            <p className="admin-error-help">{t('adminDashboard.contactAdmin')}</p>
            <button
                className="admin-error-button"
                onClick={() => navigate('/')}
            >
              {t('adminDashboard.returnHome')}
            </button>
          </div>
        </div>
    );
  }

  return (
      <div className="admin-dashboard">
        <div className="admin-header">
          <h1 className="admin-title">{t('adminDashboard.title')}</h1>
        </div>

        <Container fluid className="admin-container">
          <Tab.Container id="admin-tabs" activeKey={activeTab} onSelect={setActiveTab}>
            <Row>
              <Col lg={2} md={3}>
                <div className="admin-sidebar">
                  <Nav variant="pills" className="flex-column admin-nav">
                    <Nav.Item>
                      <Nav.Link
                          eventKey="statistics"
                          className={`admin-nav-link ${activeTab === 'statistics' ? 'active' : ''}`}
                      >
                        <FaChartBar className="admin-nav-icon" />
                        <span className="admin-nav-text">{t('adminDashboard.statistics')}</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                          eventKey="photos"
                          className={`admin-nav-link ${activeTab === 'photos' ? 'active' : ''}`}
                      >
                        <FaImage className="admin-nav-icon" />
                        <span className="admin-nav-text">{t('adminDashboard.photos')}</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                          eventKey="wishes"
                          className={`admin-nav-link ${activeTab === 'wishes' ? 'active' : ''}`}
                      >
                        <FaComment className="admin-nav-icon" />
                        <span className="admin-nav-text">{t('adminDashboard.wishes')}</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                          eventKey="users"
                          className={`admin-nav-link ${activeTab === 'users' ? 'active' : ''}`}
                      >
                        <FaUsers className="admin-nav-icon" />
                        <span className="admin-nav-text">{t('adminDashboard.users')}</span>
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link
                          eventKey="settings"
                          className={`admin-nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                      >
                        <FaCog className="admin-nav-icon" />
                        <span className="admin-nav-text">{t('adminDashboard.settings')}</span>
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>

                  <div className="admin-user-info">
                    <div className="admin-user-avatar">
                      {user.photoURL ? (
                          <img src={user.photoURL} alt={user.displayName} />
                      ) : (
                          <div className="admin-user-initial">
                            {(user.displayName || user.email || 'A').charAt(0).toUpperCase()}
                          </div>
                      )}
                    </div>
                    <div className="admin-user-details">
                      <div className="admin-user-name">
                        {user.displayName || user.email || t('adminDashboard.admin')}
                      </div>
                      <div className="admin-user-role">{t('adminDashboard.adminRole')}</div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg={10} md={9} className="admin-content">
                <div className="admin-content-header">
                  <div className="admin-breadcrumb">
                    <span>{t('adminDashboard.dashboard')}</span>
                    <span className="admin-breadcrumb-separator">›</span>
                    <span className="admin-breadcrumb-current">
                    {t(`adminDashboard.${activeTab}`)}
                  </span>
                  </div>
                  <button
                      className="admin-home-button"
                      onClick={() => navigate('/')}
                  >
                    {t('adminDashboard.backToSite')}
                  </button>
                </div>

                <div className="admin-content-body">
                  <Tab.Content>
                    <Tab.Pane eventKey="statistics">
                      <Statistics />
                    </Tab.Pane>
                    <Tab.Pane eventKey="photos">
                      <PhotoManagement />
                    </Tab.Pane>
                    <Tab.Pane eventKey="wishes">
                      <WishManagement />
                    </Tab.Pane>
                    <Tab.Pane eventKey="users">
                      <UserManagement />
                    </Tab.Pane>
                    <Tab.Pane eventKey="settings">
                      <Settings />
                    </Tab.Pane>
                  </Tab.Content>
                </div>
              </Col>
            </Row>
          </Tab.Container>
        </Container>
      </div>
  );
};

export default Dashboard;